/**
 * DeleteCompanySection: "Danger zone" on the company detail page (SF-4324,
 * epic SF-4321; scheduled deletion SF-4813).
 *
 * Visible only with `admin:companies:delete` on a company the person may change
 * (sf-api checks both again). What it shows follows the company's open deletion
 * (the `deletion` field of the company read):
 *
 *   - none: "Delete company" opens a dialog with the live impact counts and a
 *     type-the-name check; confirming SCHEDULES the deletion. The company stays
 *     switched off and is deleted after the waiting period (graceDays).
 *     SF-5158: only an inactive company can be deleted, so the button is off
 *     while the company is active (sf-api refuses it too).
 *   - scheduled: the date and a Cancel button.
 *   - running: "Deletion in progress".
 *   - failed / stopped: the errors, the attempts and a Retry button.
 *
 * Every action reports back through `onChanged(companyId, deletion)` with the
 * company it STARTED on, so the page refreshes that company even if staff have
 * moved to another one meanwhile (the route element is reused across ids).
 * mutateAsync is used because react-query drops per-call callbacks once the
 * component has unmounted.
 */
import { useState } from 'react';
import { useInvalidate, usePermissions } from '@refinedev/core';
import { Alert, Button, Card, Modal, Skeleton, Space, Typography, message, theme } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

import { hasPermission } from '../../../utils/permissions';
import { useCanEditCompany } from '../api/editableCompaniesClient';
import {
  COMPANY_DELETE_PERMISSION,
  COMPANY_STILL_ACTIVE_MESSAGE,
  attemptsLabel,
  cancelDeletionArgs,
  deleteCompanyMutationArgs,
  deleteErrorMessage,
  deletionActionDoneMessage,
  deletionActionErrorMessage,
  deletionErrors,
  formatDeletionDate,
  humanizeModelName,
  isDeletionRow,
  isStillActiveError,
  retryDeletionArgs,
  useCompanyDeleteImpact,
  useDeleteCompany,
  useDeletionAction,
  type CompanyDeleteImpact,
  type CompanyDeletionRow,
} from '../api/companyDeleteClient';
import { DeletionActionButton, type DeletionAction } from './DeletionActionButton';
import { TypeNameToConfirm } from './TypeNameToConfirm';

const { Text, Paragraph } = Typography;

export interface DeleteCompanySectionProps {
  /** Company ObjectId. */
  companyId: string;
  /** Company name from the already-loaded detail record (display fallback). */
  companyName?: string;
  /** Company `deletion_status` ('scheduled' | 'deleting'); used when no row is sent. */
  deletionStatus?: string | null;
  /** The company's open deletion from the company read (S4.6); null or missing = none. */
  deletion?: CompanyDeletionRow | null;
  /** Days between the request and the deletion, from the company read; missing = older API. */
  graceDays?: number | null;
  /**
   * SF-5158: the company's Active setting from the page's record. Anything but false counts as
   * active (a company stored without it is active on the server), and an active company can't
   * be deleted.
   */
  isActive?: boolean | null;
  /** This company's read is still on its way: show a placeholder. */
  loading?: boolean;
  /**
   * This company's read failed and there is no record of it to show: 'not-found'
   * for a 404, 'failed' otherwise. Either way no action is offered.
   */
  loadError?: 'not-found' | 'failed' | null;
  /** Read the company again (the Retry on a 'failed' read). */
  onRetryLoad?: () => void;
  /** That re-read is running. */
  retryingLoad?: boolean;
  /**
   * Called after a change was saved, with the company it was for and the new open
   * deletion when the server sent it (null after a cancel).
   */
  onChanged?: (companyId: string, deletion?: CompanyDeletionRow | null) => void;
}

/** Which state the danger zone is in. */
type SectionState = 'none' | 'scheduled' | 'running' | 'failed' | 'stopped';

function sectionState(
  deletion: CompanyDeletionRow | null | undefined,
  status?: string | null,
): SectionState {
  if (deletion) {
    const s = deletion.status;
    if (s === 'scheduled' || s === 'running' || s === 'failed' || s === 'stopped') return s;
    return 'none';
  }
  // An older sf-api sends no row: fall back to the company's own flag.
  if (status === 'scheduled') return 'scheduled';
  if (status === 'deleting') return 'running';
  return 'none';
}

/** Permission-gated, state-aware danger zone. */
export const DeleteCompanySection: React.FC<DeleteCompanySectionProps> = ({
  companyId,
  companyName,
  deletionStatus,
  deletion,
  graceDays: graceDaysFromCompany,
  isActive,
  loading,
  loadError,
  onRetryLoad,
  retryingLoad,
  onChanged,
}) => {
  const invalidate = useInvalidate();

  const { data: permissions } = usePermissions<string[]>();
  // SF-4668: staff with a company list may only delete companies on it.
  const { canEditCompany } = useCanEditCompany(companyId);
  const canDelete = hasPermission(permissions ?? [], COMPANY_DELETE_PERMISSION) && canEditCompany;
  // Danger styling via the theme's error token (follow src/config/theme.ts
  // tokens rather than hardcoding antd's default red).
  const { token } = theme.useToken();

  const [modalOpen, setModalOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Impact counts are fetched live only once the modal opens, so the dialog
  // always reflects the current state rather than page-load-time counts.
  const impactQuery = useCompanyDeleteImpact(companyId, { enabled: canDelete && modalOpen });
  const impact: CompanyDeleteImpact | undefined = impactQuery.data?.data;
  const deleteCompany = useDeleteCompany();
  const deletionAction = useDeletionAction();

  if (!canDelete) return null;

  const state = sectionState(deletion, deletionStatus);

  // The confirm target comes from the live impact response; fall back to the
  // loaded detail record so the prompt still reads sensibly while fetching.
  const targetName = impact?.companyName ?? companyName ?? '';
  // The only way to Schedule deletion (button and handler alike): a settled,
  // successful impact read for THIS company (a cached one being re-read, or
  // whose re-read failed, does not count), proof the API schedules rather than
  // purging at once (graceDays), the exact case-sensitive name (AC3), and no
  // request already on its way.
  const impactSettled =
    !!impact &&
    !impactQuery.isLoading &&
    !impactQuery.isFetching &&
    !impactQuery.isError &&
    String(impact.company_id).toLowerCase() === companyId.toLowerCase();
  // SF-5158: the page's record decides the Delete button. In the dialog the live impact read
  // (when the server sends isActive) can only add a refusal, never lift the page's.
  const activeOnPage = isActive !== false;
  const stillActive = activeOnPage || impact?.isActive === true;
  const canSchedule = impactSettled && typeof impact.graceDays === 'number' && !stillActive;
  const canConfirm = canSchedule && typedName === impact.companyName && !deleteCompany.isLoading;

  const openModal = () => {
    setTypedName('');
    setErrorText(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (deleteCompany.isLoading) return;
    setModalOpen(false);
  };

  const handleConfirm = () => {
    // AC4: never fires unless the button could, even if its state were bypassed
    // (e.g. Enter key while focused) or a double-click raced its loading state.
    if (!canConfirm) return;
    setErrorText(null);
    const forCompany = companyId;
    deleteCompany
      .mutateAsync(deleteCompanyMutationArgs(forCompany, typedName))
      .then((result) => {
        setModalOpen(false);
        invalidate({ resource: 'companies', invalidates: ['list'] });
        const row = result?.data;
        // Only an API that schedules gets here (canConfirm needs graceDays). A
        // reply without a row just re-reads the company to show where it stands.
        message.success(
          isDeletionRow(row)
            ? `Deletion scheduled for ${formatDeletionDate(row.delete_after)}.`
            : 'Deletion scheduled.',
        );
        onChanged?.(forCompany, isDeletionRow(row) ? row : undefined);
      })
      .catch((error: unknown) => {
        setErrorText(deleteErrorMessage(error));
        // SF-5158: switched back on since the page read it: re-read it so the button greys.
        if (isStillActiveError(error)) onChanged?.(forCompany);
      });
  };

  const runAction = (kind: DeletionAction) => {
    if (deletionAction.isLoading) return;
    setActionError(null);
    const forCompany = companyId;
    const args = kind === 'cancel' ? cancelDeletionArgs(forCompany) : retryDeletionArgs(forCompany);
    deletionAction
      .mutateAsync(args)
      .then((result) => {
        message.success(deletionActionDoneMessage(kind));
        // A cancel can answer `data: null` (it only repaired the company's flag):
        // either way there is no open deletion now.
        const row = result?.data;
        onChanged?.(forCompany, kind === 'cancel' ? null : isDeletionRow(row) ? row : undefined);
      })
      .catch((error: unknown) => {
        setActionError(deletionActionErrorMessage(error, kind));
        // A 409 means what is on screen is out of date: re-read it.
        onChanged?.(forCompany);
      })
      .finally(() => invalidate({ resource: 'companies', invalidates: ['list'] }));
  };

  const graceDays = impact?.graceDays;
  // The card shows the days before the dialog opens; the company read carries them (SF-4812).
  const cardGraceDays = graceDaysFromCompany ?? graceDays;
  const impactContent = impactQuery.isLoading ? (
    // Loading ≠ empty: never show counts (or "nothing to delete") mid-fetch.
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : impactQuery.isError ? (
    <Alert
      type="error"
      showIcon
      message="Could not load the deletion impact summary."
      description="Close this dialog and try again."
    />
  ) : impact && typeof graceDays !== 'number' ? (
    // An sf-api without SF-4812 would purge at once, with no waiting period and
    // nothing to cancel: never send it the request.
    <Alert
      type="warning"
      showIcon
      message="Scheduled deletion is not available yet. Please try again after the API update."
    />
  ) : impact && stillActive ? (
    <Alert type="warning" showIcon message={COMPANY_STILL_ACTIVE_MESSAGE} />
  ) : impact && typeof graceDays === 'number' ? (
    <>
      <Alert
        type="error"
        showIcon
        icon={<ExclamationCircleOutlined />}
        message="Its people can't sign in once you confirm"
        description={`After ${graceDays} day${
          graceDays === 1 ? '' : 's'
        } it is deleted for good, with all the data below. You can cancel until then. ${
          impact.total
        } record${impact.total === 1 ? '' : 's'} across ${
          impact.collections.length
        } collection${impact.collections.length === 1 ? '' : 's'}.`}
      />
      {impact.collections.length > 0 ? (
        <ul style={{ margin: '12px 0', paddingLeft: 20 }} aria-label="Deletion impact">
          {impact.collections.map(({ model, count }) => (
            <li key={model}>
              <Text>
                {humanizeModelName(model)}: <Text strong>{count}</Text>
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Paragraph style={{ margin: '12px 0' }}>
          No connected records were found, so only the company itself will be deleted.
        </Paragraph>
      )}
      {(impact.manual_steps ?? []).length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Manual cleanup required"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {impact.manual_steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      <TypeNameToConfirm
        name={impact.companyName}
        ariaLabel="Type the company name to confirm deletion"
        value={typedName}
        onChange={(value) => {
          setTypedName(value);
          setErrorText(null);
        }}
        disabled={deleteCompany.isLoading}
      />
    </>
  ) : null;

  const actionButton = (kind: DeletionAction) => (
    <DeletionActionButton
      action={kind}
      companyName={companyName}
      loading={deletionAction.isLoading}
      onConfirm={() => runAction(kind)}
    />
  );

  const errors = deletion ? deletionErrors(deletion) : [];
  // A failed read is not a load in progress: say why nothing is offered.
  const body =
    loadError === 'not-found' ? (
      <Alert type="warning" showIcon message="Company not found" />
    ) : loadError ? (
      <Alert
        type="error"
        showIcon
        message="Could not load this company."
        action={
          onRetryLoad ? (
            <Button size="small" loading={retryingLoad} onClick={onRetryLoad}>
              Retry
            </Button>
          ) : undefined
        }
      />
    ) : loading ? (
      <Skeleton active paragraph={{ rows: 1 }} />
    ) : state === 'scheduled' ? (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={
            deletion?.delete_after
              ? `Scheduled for deletion on ${formatDeletionDate(deletion.delete_after)}`
              : 'Scheduled for deletion'
          }
          description="The company is switched off and its people can't sign in. You can cancel until then."
        />
        {actionButton('cancel')}
      </Space>
    ) : state === 'running' ? (
      <Alert
        type="info"
        showIcon
        message="Deletion in progress"
        description="The company and its data are being deleted now."
      />
    ) : state === 'failed' || state === 'stopped' ? (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          type="error"
          showIcon
          message={state === 'failed' ? 'Deletion failed' : 'Deletion stopped'}
          description={
            <>
              {errors.length > 0 ? (
                <ul style={{ margin: '0 0 8px', paddingLeft: 20 }} aria-label="Deletion errors">
                  {errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              ) : null}
              <Text>Attempts: {attemptsLabel(deletion?.attempts)}.</Text>
              {deletion?.auto_retry_pending ? (
                <Text> It will retry on the next nightly run.</Text>
              ) : null}
            </>
          }
        />
        {deletion?.can_retry !== false ? actionButton('retry') : null}
      </Space>
    ) : (
      <>
        <Paragraph>
          Delete this company and <Text strong>all</Text> its connected data for good after{' '}
          {typeof cardGraceDays === 'number'
            ? `${cardGraceDays} day${cardGraceDays === 1 ? '' : 's'}`
            : 'the waiting period'}
          . It must be marked inactive first. Scheduling the deletion stops its people signing in.
          You can cancel until then.
        </Paragraph>
        <Space wrap>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={activeOnPage}
            aria-describedby={activeOnPage ? 'delete-company-still-active' : undefined}
            onClick={openModal}
          >
            Delete company
          </Button>
          {activeOnPage ? (
            <Text type="secondary" id="delete-company-still-active">
              {COMPANY_STILL_ACTIVE_MESSAGE}
            </Text>
          ) : null}
        </Space>
      </>
    );

  return (
    <>
      <Card
        title={<Text type="danger">Danger zone</Text>}
        style={{ borderColor: token.colorError }}
        styles={{ header: { borderBottomColor: token.colorError } }}
      >
        {body}
        {actionError ? (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={actionError} />
        ) : null}
      </Card>

      <Modal
        title={`Delete ${targetName || 'company'}?`}
        open={modalOpen}
        onCancel={closeModal}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={closeModal} disabled={deleteCompany.isLoading}>
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            disabled={!canConfirm}
            loading={deleteCompany.isLoading}
            onClick={handleConfirm}
          >
            Schedule deletion
          </Button>,
        ]}
      >
        {impactContent}
        {errorText ? (
          <Alert type="error" showIcon style={{ marginTop: 12 }} message={errorText} />
        ) : null}
      </Modal>
    </>
  );
};
